/**
 * Shared resilience constants, UI states, and structured logging for skin flow.
 */

import { recordSkinTelemetry } from './skinTelemetry';

export const TIMEOUT_MS = {
  DEFAULT: 20000,
  BATCH_DETECT: 180000,
  SINGLE_DETECT: 120000,
  REPORT: 30000,
  SAVE_ANALYSIS: 45000,
  FLOW_STAGE: 6000,
};

export const RETRY = {
  MAX_ATTEMPTS: 3,
  BASE_DELAY_MS: 1000,
  MAX_DELAY_MS: 8000,
  JITTER_MS: 250,
};

/** UI-facing request lifecycle states */
export const REQUEST_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  RETRYING: 'retrying',
  QUEUED: 'queued',
  TIMEOUT: 'timeout',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
  RETRY_AVAILABLE: 'retry_available',
};

const RETRYABLE_HTTP = new Set([502, 503, 504]);

export function logSkinResilience(event, details = {}) {
  const payload = { event, ts: new Date().toISOString(), ...details };
  if (event === 'retry_exhausted' || event === 'timeout' || event === 'failed') {
    console.warn('[skin-resilience]', payload);
  } else {
    console.info('[skin-resilience]', payload);
  }
  if (event === 'retry' || event === 'timeout' || event === 'reconnect' || event === 'retry_exhausted' || event === 'abort') {
    recordSkinTelemetry(event, details);
  }
}

export function isAbortError(err) {
  return err?.name === 'AbortError' || err?.code === 'ABORT_ERR';
}

export function isTimeoutError(err) {
  return err?.name === 'AbortError' && err?.reason === 'timeout';
}

export function isRetryableHttpStatus(status) {
  return RETRYABLE_HTTP.has(Number(status));
}

export function isRetryableApiPayload(data) {
  return Boolean(data && data.retryable === true);
}

export function isRetryableError(err, data = null) {
  if (isAbortError(err) && !isTimeoutError(err)) return false;
  if (isTimeoutError(err)) return true;
  if (err?.retryable === true) return true;
  if (isRetryableHttpStatus(err?.status)) return true;
  if (isRetryableApiPayload(data)) return true;
  if (err?.name === 'TypeError') return true; // network / CORS / offline
  if (err?.message && /failed to fetch|network|load failed/i.test(err.message)) return true;
  return false;
}

export function computeBackoffMs(attempt, baseMs = RETRY.BASE_DELAY_MS, maxMs = RETRY.MAX_DELAY_MS) {
  const exp = Math.min(maxMs, baseMs * 2 ** attempt);
  const jitter = Math.floor(Math.random() * RETRY.JITTER_MS);
  return exp + jitter;
}

export function sleep(ms, signal) {
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Create a linked abort scope; call scope.abort() to cancel in-flight work. */
export function createRequestScope() {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    abort: (reason) => {
      try {
        controller.abort(reason);
      } catch (_) {
        controller.abort();
      }
    },
    isAborted: () => controller.signal.aborted,
  };
}
